import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  Flex,
  Badge
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import HeaderMenus from '../HeaderMenus';

const ProjectList = () => {
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await callApi('/api/projects', accessToken);
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchProjects();
    }
  }, [accessToken]);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    
    setIsCreating(true);
    try {
      const response = await callApi('/api/projects', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newProjectName })
      });
      if (!response.ok) throw new Error('Failed to create project');
      const data = await response.json();
      
      onClose();
      setNewProjectName('');
      navigate(`/projects/${data.id}`);
    } catch (err) {
      setError(err.message || 'Failed to create project');
      setIsCreating(false);
    }
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1200px" margin="0 auto">
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <Heading size="lg">Project Workspaces</Heading>
        <Button colorScheme="blue" onClick={onOpen}>
          Create Project
        </Button>
      </Flex>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Flex justify="center" p={10}>
          <Spinner size="xl" />
        </Flex>
      ) : (
        <Box bg="white" shadow="sm" borderRadius="md" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Last Updated</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {projects.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                    No projects found. Create one to get started!
                  </Td>
                </Tr>
              ) : (
                projects.map((project) => (
                  <Tr key={project.id} _hover={{ bg: 'gray.50' }}>
                    <Td fontWeight="medium">{project.name}</Td>
                    <Td>
                      <Badge colorScheme={project.status === 'active' ? 'green' : 'gray'}>
                        {project.status}
                      </Badge>
                    </Td>
                    <Td>{new Date(project.created_at).toLocaleDateString()}</Td>
                    <Td>{new Date(project.updated_at).toLocaleDateString()}</Td>
                    <Td textAlign="right">
                      <Button size="sm" onClick={() => navigate(`/projects/${project.id}`)}>
                        View Workspace
                      </Button>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Project</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Project Name</FormLabel>
              <Input 
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="e.g., Q4 Intake Review"
                autoFocus
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleCreateProject}
              isLoading={isCreating}
              isDisabled={!newProjectName.trim()}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      </Box>
    </Box>
  );
};

export default ProjectList;